import styled from 'styled-components';

import React, { ChangeEvent, useEffect, useReducer, useState } from 'react';
import { black, exactWhite, gray } from '../../utils/style/colors';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

import moment from 'moment-jalaali';
import Select, { IOption } from '../../components/Select/Select';
import {
  handleConnectionTest,
  handleCreateUpdateHIS,
  handleFetchInitialInformation,
  handleFetchPatient,
  handleSamplePhonenumberTest,
} from '../../api/HISSMSApi';
import notify from '../../components/Notify/Notify';
import APopover from '../../components/APopover/APopover';
import Input from '../../components/Input/Input';
import {
  MultiDatePicker,
  MultiDatePicker_Range,
} from '../../components/MyDatePicker/MultiDatePicker';
import Textarea from '../../components/Textarea/Textarea';
import Collapsible from '../../components/Collapsible/Collapsible';
import Button from '../../components/Button/Button';
import { messages } from './messages';
import Icon from '../../components/Icon/Icon';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 1rem;
`;

const HISHeaderFormContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HISFormHeader = styled.h2`
  font-weight: bold;
`;

const HISMainFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0.5rem 0;
`;

const HISFilterItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: 0.5rem;
  flex-wrap: wrap;
  gap: ${(props) => (props.isCheckbox ? '2rem' : '1rem')};
  & > * {
    ${(props) => !props.isCheckbox && 'flex: 1'};
  }
`;

const HISFilterButtonRow = styled.div`
  align-self: end;
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const CreateHISIconContainer = styled.div`
  border-radius: 100%;
  height: 2rem;
  width: 2rem;
  background: ${black};
  color: var(--exact-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;

  & > span {
    padding-top: 0.25rem;
  }

  &:hover {
    cursor: pointer;
  }
`;

const PopoverContentShowup = styled.div`
  background: ${exactWhite};
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: bold;
`;

const LastUpdateContainer = styled.div`
  color: ${gray};
  font-size: 1rem;
`;

const Description = styled.div`
  margin-top: -1rem;
  padding-inline-start: 1rem;
  color: ${gray};
  font-size: 0.8rem;
`;

const CollapsibleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CollapsibleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

interface IProps {
  path: string;
}

type HISType = 'Tirajeh' | 'Sayan';
interface HISProps {
  connectionUrl: string;
  connectionUsername: string;
  connectionPassword: string;
  viewName: string;
  dayOffset: number;
  smsUrl: string;
  smsMessage: string;
  validDate: number;
  hisType: HISType;
}

interface FetchStatus {
  lastSendDate: string;
  lastRId: number;
}

type UUID = string;

export interface IHISInformationState {
  id: UUID;
  name: string;
  hisProps: HISProps;
  enabled: boolean;
  deleted: boolean;
  fetchStatus: FetchStatus;
}

const initialHISPropsInformation: HISProps = {
  connectionUrl: '',
  connectionUsername: '',
  connectionPassword: '',
  viewName: '',
  dayOffset: 1,
  smsUrl: '',
  smsMessage: '',
  validDate: new Date().getTime(),
  hisType: 'Tirajeh',
};

interface IAction {
  type: 'reset' | 'update' | 'create';
  payload?: Partial<HISProps>;
}

const HISPropsReducer = (state: HISProps, action: IAction) => {
  switch (action.type) {
    case 'reset':
      return initialHISPropsInformation;
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
    case 'create':
      return {
        ...initialHISPropsInformation,
        ...action.payload,
      };
    default:
      return state;
  }
};

const hisTypes: IOption[] = [
  { label: 'تیراژه', value: 'Tirajeh' },
  { label: 'سایان', value: 'Sayan' },
];

const HISSMSConfiguration: React.FC<IProps> = ({ path: _ }) => {
  const thisUser = useSelector((state: RootState) => state.thisUser);

  const [stateProps, dispatch] = useReducer(
    HISPropsReducer,
    initialHISPropsInformation
  );

  const [allHospital, setAllHospital] = useState<IHISInformationState[]>([]);
  const [selectedHospital, setSelectedHospital] =
    useState<IHISInformationState | null>(null);

  const [HISName, setHISName] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);

  const [isCreating, setIsCreating] = useState<boolean>(false);

  //@todo need to combine all of loading as a signle state
  const [loading, setLoading] = useState<
    | 'confirmFormLoading'
    | 'generalTestLoading'
    | 'loadingCount'
    | 'phoneTestLoading'
    | null
  >();

  const [includeDeleted, setIncludeDeleted] = useState<boolean>(true);
  const [patientDuration, setPatientDuration] = useState({
    startDate: moment().startOf('jMonth').valueOf(),
    dueDate: moment().endOf('jMonth').valueOf(),
  });
  const [patientCount, setPatientCount] = useState<number | undefined>(
    undefined
  );

  const [testPhoneNumber, setTestPhoneNumber] = useState<string>('');

  const hospitalOptions = (
    includeDeleted
      ? allHospital
      : allHospital.filter((hospital) => !hospital.deleted)
  ).reduce((acc: IOption[], hospital) => {
    acc.push({
      label: `${hospital.deleted ? '❌' : hospital.enabled ? '✅' : ''} ${hospital.name}`,
      value: hospital.id,
    });
    return acc;
  }, []);

  const handleResetForm = () => {
    setSelectedHospital(null);
    setHISName('');
    setEnabled(false);
    setDeleted(false);
    dispatch({
      type: 'reset',
    });
  };

  const handleSubmitForm = async () => {
    const HISInitialInformation = {
      name: HISName,
      enabled,
      deleted,
    };
    try {
      setLoading('confirmFormLoading');
      await handleCreateUpdateHIS(
        isCreating
          ? { ...HISInitialInformation, hisProps: stateProps }
          : {
              ...selectedHospital,
              ...HISInitialInformation,
              hisProps: stateProps,
            },
        thisUser
      );
      notify(messages.newHISAddedSuccessfully);
    } catch (_: any) {
      notify(messages.newHISFailed);
    } finally {
      setLoading(null);
    }
  };

  const handleChangeTest = async (id: string) => {
    try {
      setLoading('generalTestLoading');
      const result: string = await handleConnectionTest(id, thisUser);
      if (result.toLowerCase() === 'success') {
        notify(messages.connected);
      } else {
        notify(messages.failedToConnect, 'error');
      }
    } catch (e: any) {
      notify(messages.failedToConnect, 'error');
    } finally {
      setLoading(null);
    }
  };

  const handleFetchPatienDuration = async () => {
    setLoading('loadingCount');
    const count = await handleFetchPatient(
      patientDuration.startDate,
      patientDuration.dueDate,
      selectedHospital?.id as string,
      thisUser
    );
    setLoading(null);
    setPatientCount(count || 0);
  };

  const handlePhoneTest = async () => {
    try {
      setLoading('phoneTestLoading');
      await handleSamplePhonenumberTest(
        selectedHospital!.id,
        testPhoneNumber,
        thisUser
      );
    } catch (e: any) {
      /*
      console.log(e);
*/
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => {
    if (!loading) {
      const fetchData = async () => {
        const hospitals = await handleFetchInitialInformation(
          thisUser,
          includeDeleted
        );
        setAllHospital(hospitals);
      };
      fetchData();
    }
  }, [loading]);

  useEffect(() => {
    handleResetForm();
  }, [isCreating]);

  useEffect(() => {
    if (selectedHospital) {
      setHISName(selectedHospital.name);
      setEnabled(selectedHospital.enabled);
      setDeleted(selectedHospital.deleted);
      setPatientDuration({
        startDate: moment().startOf('jMonth').valueOf(),
        dueDate: moment().endOf('jMonth').valueOf(),
      });
      setIncludeDeleted(true);
      setPatientCount(undefined);
      dispatch({
        type: 'update',
        payload: selectedHospital.hisProps,
      });
      handleFetchPatient(
        patientDuration.startDate,
        patientDuration.dueDate,
        selectedHospital.id,
        thisUser
      );
    }
  }, [selectedHospital]);

  return (
    <PageContainer>
      <HISHeaderFormContainer>
        <HISFormHeader>{messages.HISHeader}</HISFormHeader>
        <APopover
          placement={'right'}
          trigger={'hover'}
          target={
            <CreateHISIconContainer>
              {isCreating ? (
                <Icon
                  onClick={() => setIsCreating(false)}
                  style={{ fontSize: '1rem' }}
                  type={'edit-2'}
                />
              ) : (
                <span onClick={() => setIsCreating(true)}>+</span>
              )}
            </CreateHISIconContainer>
          }
        >
          <PopoverContentShowup>
            {isCreating ? messages.update : messages.create}
          </PopoverContentShowup>
        </APopover>
      </HISHeaderFormContainer>
      <HISMainFilterContainer>
        <HISFilterItemRow>
          {!isCreating ? (
            <>
              <Select
                options={hospitalOptions}
                values={hospitalOptions.filter(
                  (option) => option.value === selectedHospital?.id
                )}
                onChangeValues={(options: IOption[]) => {
                  const selectedHospitalOption = allHospital.find(
                    (hospital) => hospital.id === options[0].value
                  );
                  if (selectedHospitalOption)
                    setSelectedHospital(selectedHospitalOption);
                }}
                multiChoice={false}
              />
              <Input
                type="checkbox"
                id="includeDeleted"
                label={messages.includeDeleted}
                checked={includeDeleted}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setIncludeDeleted(e.target.checked)
                }
                wrapperStyle={{ flexBasis: '13rem', flexGrow: 0 }}
              />
            </>
          ) : (
            <></>
          )}
        </HISFilterItemRow>
        <HISFilterItemRow isCheckbox={true}>
          <Input
            label={messages.enabled}
            type={'checkbox'}
            id={'HISEnable'}
            checked={enabled}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setEnabled(event.target.checked)
            }
          />
          {!isCreating && (
            <Input
              label={messages.deleted}
              type={'checkbox'}
              id={'HISDeleted'}
              checked={deleted}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setDeleted(event.target.checked)
              }
            />
          )}
        </HISFilterItemRow>
        <HISFilterItemRow>
          <Input
            id={'HISName'}
            label={messages.HISName}
            value={HISName}
            type="text"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setHISName(e.target.value)
            }
          />
          <Select
            label={messages.hisType}
            values={hisTypes.filter((it) => it.value === stateProps.hisType)}
            options={hisTypes}
            onChangeValues={(e: IOption[]) =>
              dispatch({
                type: 'update',
                payload: { hisType: e[0].value as HISType },
              })
            }
            allowRemove={false}
          />
        </HISFilterItemRow>
        <HISFilterItemRow>
          <Input
            style={{ width: '100%' }}
            type={'text'}
            id={'HISUrl'}
            label={messages.HISUrl}
            placeholder={messages.connectionUrlPlaceholder}
            value={stateProps.connectionUrl}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'update',
                payload: { connectionUrl: e.target.value },
              })
            }
          />
        </HISFilterItemRow>
        <HISFilterItemRow>
          <Input
            style={{ width: '100%' }}
            type={'text'}
            id={'connectionUsername'}
            label={messages.connectionUsername}
            value={stateProps.connectionUsername}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'update',
                payload: { connectionUsername: e.target.value },
              })
            }
          />
          <Input
            style={{ width: '100%' }}
            type={'text'}
            id={'HISPassword'}
            label={messages.connectionPassword}
            value={stateProps.connectionPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'update',
                payload: { connectionPassword: e.target.value },
              })
            }
          />
          <Input
            style={{ width: '100%' }}
            type={'text'}
            id={'viewName'}
            label={messages.viewName}
            value={stateProps.viewName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'update',
                payload: { viewName: e.target.value },
              })
            }
          />
        </HISFilterItemRow>
        <HISFilterItemRow>
          <Input
            style={{ width: '100%' }}
            type={'number'}
            min={1}
            id={messages.dayOffset}
            label={messages.dayOffset}
            value={stateProps.dayOffset}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'update',
                payload: { dayOffset: Number(e.target.value) },
              })
            }
          />
          <MultiDatePicker
            id={'validDate'}
            setDate={(value) => {
              dispatch({
                type: 'update',
                payload: { validDate: Number(value) },
              });
            }}
            placeholder={'validDate'}
            label={messages.validDate}
            value={stateProps.validDate}
            hasTime={false}
          />
        </HISFilterItemRow>
        <HISFilterItemRow>
          <Input
            style={{ width: '100%' }}
            type={'text'}
            id={'smsUrl'}
            label={messages.smsUrl}
            value={stateProps.smsUrl}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'update',
                payload: { smsUrl: e.target.value },
              })
            }
          />
        </HISFilterItemRow>
        <Description>{messages.smsUrlExp}</Description>
        <HISFilterItemRow>
          <Textarea
            id={'smsMessage'}
            label={messages.smsMessage}
            value={stateProps.smsMessage}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              dispatch({
                type: 'update',
                payload: { smsMessage: event.target.value },
              });
            }}
          />
        </HISFilterItemRow>
        {selectedHospital && !isCreating && (
          <>
            <Collapsible header={<div>{messages.smsStatics}</div>}>
              <CollapsibleContainer>
                <CollapsibleRow>
                  <MultiDatePicker_Range
                    isEditable={true}
                    hasTime={false}
                    label={messages.HISPatientPeriods}
                    values={{
                      startDate: patientDuration.startDate as number,
                      dueDate: patientDuration.dueDate as number,
                    }}
                    setDate={(values) => {
                      const startDate = values.startDate;
                      const dueDate = values.dueDate;
                      setPatientDuration({
                        startDate: startDate as number,
                        dueDate: dueDate as number,
                      });
                    }}
                  />
                  <div style={{ marginInlineStart: 'auto' }}>
                    {messages.PatientCount + patientCount}
                  </div>
                </CollapsibleRow>
                <HISFilterButtonRow>
                  <Button
                    color={'black-outline'}
                    onClick={() => handleFetchPatienDuration()}
                    loading={loading === 'loadingCount'}
                  >
                    {messages.findPatient}
                  </Button>
                </HISFilterButtonRow>
              </CollapsibleContainer>
            </Collapsible>
            <Collapsible
              header={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <div>{messages.samplePhoneTest}</div>
                  <span className="text-sm"> ({messages.testphoneExp}) </span>
                </div>
              }
            >
              <CollapsibleContainer>
                <CollapsibleRow>
                  <Input
                    id={'samplePhonenumberTest'}
                    label={messages.samplePhonenumber}
                    value={testPhoneNumber}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setTestPhoneNumber(e.target.value)
                    }
                    type="text"
                  />
                </CollapsibleRow>
                <HISFilterButtonRow>
                  <Button
                    color={'black-outline'}
                    onClick={() => handlePhoneTest()}
                    loading={loading === 'phoneTestLoading'}
                  >
                    {messages.testSamplePhoneNumber}
                  </Button>
                </HISFilterButtonRow>
              </CollapsibleContainer>
            </Collapsible>
          </>
        )}
        <HISFilterButtonRow
          style={{ paddingInlineEnd: '5rem', marginBottom: '2rem' }}
        >
          {selectedHospital &&
            !isCreating &&
            selectedHospital.fetchStatus.lastSendDate && (
              <LastUpdateContainer>
                ({messages.lastUpdate} :{' '}
                {selectedHospital.fetchStatus.lastSendDate})
              </LastUpdateContainer>
            )}
          {selectedHospital && !isCreating && (
            <Button
              color={'black'}
              onClick={() => handleChangeTest(selectedHospital?.id)}
              loading={loading === 'generalTestLoading'}
            >
              {messages.test}
            </Button>
          )}
          <Button
            color={'black'}
            onClick={handleSubmitForm}
            loading={loading === 'confirmFormLoading'}
          >
            {isCreating ? messages.create : messages.update}
          </Button>
        </HISFilterButtonRow>
      </HISMainFilterContainer>
    </PageContainer>
  );
};

export default HISSMSConfiguration;
